import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../lib/apiClient';
import { Button } from '../ui/button';
import { Code, Briefcase, GraduationCap, BookOpen, ArrowRight, TrendingUp } from 'lucide-react';

const DashboardHome = () => {
  const [stats, setStats] = useState({
    skills: 0,
    projects: 0,
    experience: 0,
    blog: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const [skills, projects, experience, blog] = await Promise.all([
        apiClient.get('/api/admin/skills'),
        apiClient.get('/api/admin/projects'),
        apiClient.get('/api/admin/experience'),
        apiClient.get('/api/admin/blog'),
      ]);

      setStats({
        skills: skills?.length || 0,
        projects: projects?.length || 0,
        experience: experience?.length || 0,
        blog: blog?.length || 0,
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { label: 'Skills', value: stats.skills, icon: Code, link: '/admin/skills', color: 'cyan' },
    { label: 'Projects', value: stats.projects, icon: Briefcase, link: '/admin/projects', color: 'amber' },
    { label: 'Experience', value: stats.experience, icon: GraduationCap, link: '/admin/experience', color: 'green' },
    { label: 'Blog Posts', value: stats.blog, icon: BookOpen, link: '/admin/blog', color: 'purple' },
  ];

  const quickActions = [
    { label: 'Update Personal Info', link: '/admin/personal-info', description: 'Edit your bio and contact details' },
    { label: 'Add New Project', link: '/admin/projects', description: 'Showcase your latest work' },
    { label: 'Write Blog Post', link: '/admin/blog', description: 'Share your knowledge and insights' },
    { label: 'Update Philosophy', link: '/admin/philosophy', description: 'Define your development principles' },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard-home">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">Welcome Back!</h1>
        <p className="text-neutral-400">
          Manage your portfolio content from this dashboard
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.label}
              to={stat.link}
              className="group"
              data-testid={`stat-card-${stat.label.toLowerCase()}`}
            >
              <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-6 hover:border-cyan-500/50 transition-all hover:transform hover:scale-105">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-lg bg-${stat.color}-500/10 border border-${stat.color}-500/20`}>
                    <Icon className={`w-6 h-6 text-${stat.color}-500`} />
                  </div>
                  <ArrowRight className="w-5 h-5 text-neutral-600 group-hover:text-cyan-500 transition-colors" />
                </div>
                <p className="text-3xl font-bold text-white mb-1">
                  {loading ? '...' : stat.value}
                </p>
                <p className="text-sm text-neutral-400">{stat.label}</p>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <TrendingUp size={24} className="text-cyan-500" />
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {quickActions.map((action) => (
            <Link
              key={action.label}
              to={action.link}
              className="group bg-neutral-900 border border-neutral-800 rounded-xl p-6 hover:border-cyan-500/50 transition-all"
              data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                {action.label}
              </h3>
              <p className="text-sm text-neutral-400">{action.description}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-3">💡 Pro Tips</h3>
        <ul className="space-y-2 text-sm text-neutral-300">
          <li>• Keep your projects up to date with recent work</li>
          <li>• Use the <code className="px-2 py-1 bg-neutral-800 rounded">order_num</code> field to control display order</li>
          <li>• Mark projects as <code className="px-2 py-1 bg-neutral-800 rounded">featured</code> to show on homepage</li>
          <li>• Set blog posts to <code className="px-2 py-1 bg-neutral-800 rounded">published: false</code> to save as drafts</li>
        </ul>
      </div>
    </div>
  );
};

export default DashboardHome;
